/**
 * DashAssistant - React Component for Dash Integration
 * 
 * This is a React wrapper around AssistantOverlay for use in Dash applications.
 * Props match the Python wrapper (DashAssistant.py) for proper Dash integration.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { AssistantOverlay } from '../AssistantOverlay';

/**
 * DashAssistant component for Dash
 * 
 * Props are matched to the Python wrapper:
 * - opened: Whether panel is open (maps to is_open internally)
 * - active_tab: Currently active tab
 * - tabs: Object with tab visibility { context, chat, graph, artefacts }
 * - options: Object with { theme, position, width, show_trigger }
 * - workspace_id, canvas_id, cursor_id: Context IDs
 * - api_endpoint, access_token: API configuration
 * - page_context: Page context data
 * - layout_mode: 'compact' or 'full'
 */
const DashAssistant = ({
    id,
    opened,
    active_tab,
    workspace_id,
    canvas_id,
    cursor_id,
    tabs,
    layout_mode,
    page_context,
    options,
    api_endpoint,
    access_token,
    className,
    setProps,
}) => {
    const containerRef = useRef(null);
    const overlayRef = useRef(null);
    const triggerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(opened || false);

    // Extract options with defaults
    const theme = options?.theme || 'light';
    const position = options?.position || 'right';
    const width = options?.width || '420px';
    const showTrigger = options?.show_trigger !== false;

    // Extract tab visibility with defaults
    const showContext = tabs?.context !== false;
    const showChat = tabs?.chat !== false;
    const showExplorer = tabs?.graph !== false;
    const showArtefacts = tabs?.artefacts !== false;

    // Toggle handler
    const handleToggle = useCallback(() => {
        if (overlayRef.current) {
            overlayRef.current.toggle();
            setIsOpen(overlayRef.current.isOpen);
            if (setProps) {
                setProps({ opened: overlayRef.current.isOpen });
            }
        }
    }, [setProps]);

    // Create trigger button
    useEffect(() => {
        if (!showTrigger) return;

        // Remove existing trigger if present
        const existing = document.getElementById('merlin-assistant-trigger');
        if (existing) existing.remove();

        // Create trigger button (Trusted Types safe - no innerHTML)
        const trigger = document.createElement('button');
        trigger.id = 'merlin-assistant-trigger';
        trigger.setAttribute('aria-label', 'Open Faxiom Assistant');
        const triggerImg = document.createElement('img');
        triggerImg.src = 'data:image/png;base64,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';
        triggerImg.alt = 'Assistant';
        triggerImg.style.cssText = 'width: 40px; height: 40px; object-fit: contain;';
        trigger.appendChild(triggerImg);
        
        trigger.style.cssText = `
            position: fixed;
            bottom: 24px;
            ${position === 'left' ? 'left' : 'right'}: 24px;
            width: 72px;
            height: 72px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.95);
            color: white;
            border: 3px solid #000000;
            cursor: pointer;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1);
            z-index: 2147483646;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        `;

        trigger.addEventListener('mouseenter', () => {
            trigger.style.transform = 'scale(1.1)';
            trigger.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15)';
            trigger.style.borderColor = '#000000';
        });

        trigger.addEventListener('mouseleave', () => {
            trigger.style.transform = 'scale(1)';
            trigger.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1)';
            trigger.style.borderColor = '#000000';
        });

        trigger.addEventListener('click', handleToggle);

        document.body.appendChild(trigger);
        triggerRef.current = trigger;

        return () => {
            if (triggerRef.current) {
                triggerRef.current.remove();
                triggerRef.current = null;
            }
        };
    }, [showTrigger, position, handleToggle]);

    // Initialize overlay on mount
    useEffect(() => {
        if (!containerRef.current || overlayRef.current) return;

        overlayRef.current = new AssistantOverlay({
            theme: theme,
            position: position,
            width: width,
            min_width: '440px',
            show_context: showContext,
            show_chat: showChat,
            show_explorer: showExplorer,
            show_artefacts: showArtefacts,
            api_endpoint: api_endpoint,
            workspace_id: workspace_id,
            canvas_id: canvas_id,
            access_token: access_token,
            layout_mode: layout_mode || 'compact',
        });

        // Auto-open if specified
        if (opened) {
            overlayRef.current.toggle(true);
            setIsOpen(true);
        }

        // Set active tab if specified
        if (active_tab && overlayRef.current.setActiveTab) {
            overlayRef.current.setActiveTab(active_tab);
        }

        return () => {
            if (overlayRef.current) {
                overlayRef.current.destroy();
                overlayRef.current = null;
            }
        };
    }, []);

    // Handle opened prop changes
    useEffect(() => {
        if (!overlayRef.current) return;
        overlayRef.current.toggle(opened);
    }, [opened]);

    // Handle workspace_id changes
    useEffect(() => {
        if (!overlayRef.current) return;
        if (workspace_id) {
            overlayRef.current.setWorkspace(workspace_id);
        }
    }, [workspace_id]);

    // Handle canvas_id changes
    useEffect(() => {
        if (!overlayRef.current) return;
        if (canvas_id) {
            overlayRef.current.setCanvas(canvas_id);
        }
    }, [canvas_id]);

    // Handle cursor_id changes
    useEffect(() => {
        if (!overlayRef.current) return;
        if (cursor_id) {
            overlayRef.current.setCursor(cursor_id);
        }
    }, [cursor_id]);

    // Handle theme changes from options
    useEffect(() => {
        if (!overlayRef.current) return;
        if (theme) {
            overlayRef.current.setTheme(theme);
        }
    }, [theme]);

    // Handle active_tab changes
    useEffect(() => {
        if (!overlayRef.current || !active_tab) return;
        if (overlayRef.current.setActiveTab) {
            overlayRef.current.setActiveTab(active_tab);
        }
    }, [active_tab]);

    // Handle page_context changes
    useEffect(() => {
        if (!overlayRef.current || !page_context) return;
        if (overlayRef.current.setPageContext) {
            overlayRef.current.setPageContext(page_context);
        }
    }, [page_context]);

    return (
        <div 
            id={id} 
            ref={containerRef}
            className={`dash-assistant-container ${className || ''}`}
            style={{ position: 'relative' }}
        />
    );
};

DashAssistant.propTypes = {
    /** The ID used to identify this component in Dash callbacks */
    id: PropTypes.string,

    /** Whether the panel is currently open */
    opened: PropTypes.bool,

    /** Currently active tab: 'context', 'chat', 'explorer', or 'artefacts' */
    active_tab: PropTypes.string,

    /** Current workspace ID */
    workspace_id: PropTypes.string,

    /** Current canvas ID for chat context and task scoping */
    canvas_id: PropTypes.string,

    /** Current cursor ID for explorer tab */
    cursor_id: PropTypes.string,

    /** Tab visibility configuration: { context, chat, graph, artefacts } */
    tabs: PropTypes.shape({
        context: PropTypes.bool,
        chat: PropTypes.bool,
        graph: PropTypes.bool,
        artefacts: PropTypes.bool,
    }),

    /** Layout mode: 'compact' (1/3 panel) or 'full' (full screen) */
    layout_mode: PropTypes.oneOf(['compact', 'full']),

    /** Context from current page: { title, url, entities, content } */
    page_context: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
        entities: PropTypes.array,
        content: PropTypes.string,
    }),

    /** Panel configuration: { theme, position, width, show_trigger } */
    options: PropTypes.shape({
        theme: PropTypes.oneOf(['light', 'dark']),
        position: PropTypes.oneOf(['left', 'right', 'bottom']),
        width: PropTypes.string,
        show_trigger: PropTypes.bool,
    }),

    /** Base URL for API calls */
    api_endpoint: PropTypes.string,

    /** Bearer token for API authentication */
    access_token: PropTypes.string,

    /** Additional CSS class names */
    className: PropTypes.string,

    /** Dash setProps callback */
    setProps: PropTypes.func,
};

DashAssistant.defaultProps = {
    opened: false,
    active_tab: 'chat',
    tabs: {
        context: true,
        chat: true,
        graph: true,
        artefacts: true,
    },
    layout_mode: 'compact',
    options: {
        theme: 'light',
        position: 'right',
        width: '420px',
        show_trigger: true,
    },
};

// Named export for Dash compatibility
export { DashAssistant };
export default DashAssistant;
